/* ============================================================================
   Wishlist Shopify 2.0
   ============================================================================ */

(() => {
  // --- Config -------------------------------------------------------------
  const _fetch = window.fetch ? window.fetch.bind(window) : null;
  const COOKIE_NAME = 'shop_wishlist';

  // --- Helpers cookie <→> Array ------------------------------------------
  const readCookie = (name) =>
    (document.cookie.split('; ').find(r => r.startsWith(name + '=')) || '')
      .split('=')[1] || '';

  const writeCookie = (name, value, days = 365) => {
    const exp = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${exp}; path=/; SameSite=Lax`;
  };

  const parse = (str) => {
    try { return JSON.parse(decodeURIComponent(str)) || []; }
    catch { return []; }
  };

  const stringify = (arr) => encodeURIComponent(JSON.stringify(arr));

  // --- Utils --------------------------------------------------------------
  // ID valide = chaîne de chiffres (id numérique du variant Shopify)
  const isValidVariantId = (v) => typeof v === 'string' && /^\d+$/.test(v);
  // Déduplique + filtre IDs valides
  const sanitizeIds = (ids) => [...new Set((ids || []).map(String).filter(isValidVariantId))];
  const mergeWishlists = (...lists) => sanitizeIds(lists.flat());
  const sameWishlist = (a, b) => {
    const left = sanitizeIds(a).slice().sort();
    const right = sanitizeIds(b).slice().sort();

    if (left.length !== right.length) return false;
    return left.every((id, index) => id === right[index]);
  };
  const getCustomerId = () =>
    (typeof window !== 'undefined' && window.__SHOP_CUSTOMER_ID__) || null;
  const getCustomerWishlist = () =>
    sanitizeIds((((typeof window !== 'undefined' && window.__SHOP_CUSTOMER_WISHLIST__) || []).map(String)));
  const normalizeShopifyId = (value) => {
    const id = String(value || '').trim();
    if (!id) return null;
    return id.split('/').pop();
  };
  const cleanEventPayload = (payload) => Object.entries(payload).reduce((cleaned, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }

    return cleaned;
  }, {});
  const publishNexusEvent = (eventName, payload) => {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return false;

    try {
      publish.call(window.Shopify.analytics, eventName, cleanEventPayload(payload));
      return true;
    } catch (err) {
      console.warn('Wishlist analytics event échoué:', err);
      return false;
    }
  };
  const storefrontLanguageByLocale = {
    de: 'DE',
    en: 'EN',
    es: 'ES',
    fr: 'FR',
    it: 'IT',
    nl: 'NL',
    'pt-PT': 'PT_PT'
  };
  const getStorefrontLanguage = () => {
    const locale = String(window.__SHOP_LOCALE__ || document.documentElement.lang || '').trim();
    return storefrontLanguageByLocale[locale] || null;
  };
  const getStorefrontCountry = () => {
    const country = String(window.__SHOP_COUNTRY__ || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(country) ? country : null;
  };
  const getWishlistSource = (button) => {
    if (button?.dataset.wishlistSource) return button.dataset.wishlistSource;
    return button?.closest('.wishlist-card') ? 'wishlist_page' : 'product_card';
  };
  const getLocalizedRootUrl = () => {
    const root = (window.theme && window.theme.routes && window.theme.routes.root_url) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  // Formatage prix (robuste et localisé)
  const fmtPrice = (amount, currency) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return [amount, currency].filter(Boolean).join(' ') || '';
    const cur = currency || 'EUR';
    const lang = document.documentElement.lang || 'fr-FR';
    try {
      return new Intl.NumberFormat(lang, { style: 'currency', currency: cur }).format(n);
    } catch {
      return `${n} ${cur}`;
    }
  };

  const buildSrcset = (image) => {
    if (!image) return '';

    return [298, 596]
      .map(width => image[`url${width}`] ? `${image[`url${width}`]} ${width}w` : '')
      .filter(Boolean)
      .join(', ');
  };

  const setLazyImage = (imgEl, image, alt) => {
    if (!imgEl || !image) return false;

    const width = image.width || 1;
    const height = image.height || 1;
    const src = image.url20 || image.url298 || image.url || '';
    const srcset = buildSrcset(image);

    if (!src || !srcset) return false;

    imgEl.className = imgEl.className
      .replace(/\blazyloaded\b|\blazyautosizes\b|\bls-is-cached\b/g, '')
      .trim();
    imgEl.classList.add('lazyload');
    imgEl.setAttribute('width', width);
    imgEl.setAttribute('height', height);
    imgEl.setAttribute('data-sizes', 'auto');
    imgEl.setAttribute('data-src', src);
    imgEl.setAttribute('data-srcset', srcset);
    imgEl.setAttribute('fetchpriority', 'auto');
    imgEl.setAttribute('alt', alt || image.altText || '');
    imgEl.removeAttribute('src');
    imgEl.removeAttribute('srcset');

    return true;
  };

  // --- État local ---------------------------------------------------------
  const cookieWishlist = sanitizeIds(parse(readCookie(COOKIE_NAME)).map(String));
  const customerWishlist = getCustomerWishlist();
  let wishlist = getCustomerId()
    ? mergeWishlists(customerWishlist, cookieWishlist)
    : cookieWishlist;

  // --- UI : badge ---------------------------------------------------------
  const updateBadge = () => {
    const badge = document.querySelector('.wishlist-count');
    if (!badge) return;
    const n = wishlist.length;
    badge.textContent = n;
    badge.classList.toggle('opacity-0', n === 0);
    badge.classList.toggle('opacity-100', n > 0);
  };

  // --- Persistance (cookie uniquement) ------------------------------------
  const saveWishlist = async () => {
    wishlist = sanitizeIds(wishlist);
    writeCookie(COOKIE_NAME, stringify(wishlist));
  };

  // --- Sync serveur (App Proxy) ---------------------------------------------
  async function sendWishlistDelta(variantId, isAdd) {
    const cid = getCustomerId();
    if (!cid) {
      return { ok: false, connected: false, reason: 'not_logged_in' };
    }
    const id = String(variantId || '').trim();
    if (!/^\d+$/.test(id)) {
      return { ok: false, reason: 'invalid_variant_id' };
    }

    const payload = {
      customerID: String(cid),
      variantID: id,
      action: isAdd ? 'add' : 'delete'
    };

    const res = await (_fetch || fetch)('/apps/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Wishlist proxy HTTP ${res.status} — ${text || 'no body'}`);
    }
    return res.json().catch(() => ({}));
  }

  async function syncWishlistState(nextWishlist) {
    const cid = getCustomerId();
    if (!cid) {
      return { ok: false, connected: false, reason: 'not_logged_in' };
    }

    const payload = {
      customerID: String(cid),
      action: 'sync',
      variantIDs: sanitizeIds(nextWishlist)
    };

    const res = await (_fetch || fetch)('/apps/wishlist', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Wishlist proxy HTTP ${res.status} — ${text || 'no body'}`);
    }
    return res.json().catch(() => ({}));
  }

  // --- UI : boutons (état cœur) ------------------------------------------
  const updateButtons = () => {
    document.querySelectorAll('.wishlist-btn').forEach(btn => {
      const id = String(btn.dataset.variantId || '');
      const isActive = wishlist.includes(id);

      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
      const label = isActive ? btn.dataset.removeWishlistText : btn.dataset.addWishlistText;
      if (label) btn.setAttribute('aria-label', label);

      const icon = btn.querySelector('.material-symbols-outlined, .material-icons');
      if (icon) {
        icon.textContent = isActive ? 'favorite' : 'favorite_border';
        icon.classList.toggle('text-red-600', isActive);
      }
    });
  };

  // --- État vide ----------------------------------------------------------
  const updateEmptyState = () => {
    const emptyText = document.querySelector('.wishlist-empty');
    const container = document.querySelector('.wishlist-items');
    if (!emptyText || !container) return;

    const visibleCards = container.querySelectorAll('.wishlist-card');
    const isEmpty = visibleCards.length === 0;

    // Supporte deux conventions possibles
    emptyText.classList.toggle('hidden-wishlist-text', !isEmpty);
    emptyText.classList.toggle('hidden', !isEmpty);
  };

  // --- Suppression d’une carte affichée si présente ----------------------
  const removeCardIfOnPage = (variantId) => {
    const card = document.querySelector(
      `.wishlist-card [data-variant-id="${variantId}"]`
    )?.closest('.wishlist-card');
    if (card) card.remove();
  };

  const publishWishlistEvent = (isAdd, variantId, button) => {
    const customerId = getCustomerId();
    const dataset = button?.dataset || {};

    publishNexusEvent(isAdd ? 'nexus:wishlist_add' : 'nexus:wishlist_remove', {
      variant_id: normalizeShopifyId(variantId),
      product_id: normalizeShopifyId(dataset.productId),
      product_title: dataset.productTitle || null,
      product_handle: dataset.productHandle || null,
      variant_title: dataset.variantTitle || null,
      locale: window.__SHOP_LOCALE__ || document.documentElement.lang || null,
      country: getStorefrontCountry(),
      customer_id: customerId ? String(customerId) : null,
      wishlist_count: wishlist.length,
      source: getWishlistSource(button)
    });
  };

  // --- Toggle d’un variant ------------------------------------------------
  const toggleItem = (variantId, button = null) => {
    const id = String(variantId || '');
    if (!isValidVariantId(id)) return;

    const i = wishlist.indexOf(id);
    const isAdd = i === -1;

    if (isAdd) wishlist.push(id);
    else wishlist.splice(i, 1);

    saveWishlist().then(updateBadge);
    publishWishlistEvent(isAdd, id, button);
    updateButtons();
    removeCardIfOnPage(id);
    setTimeout(updateEmptyState, 0);

    // --- Appel serveur (sync App Proxy) ---
    // Non bloquant (on ignore les erreurs réseau pour garder l’UX fluide)
    sendWishlistDelta(id, isAdd).catch(err => {
      console.warn('Wishlist sync serveur échouée:', err.message);
    });
  };

  // --- Rendu d’une carte depuis le template (DOM sûr) --------------------
  const renderWishlistCard = (variant) => {
    if (!variant || !variant.product) return null;

    const tpl = document.getElementById('wishlist-product-card-template');
    if (!tpl || !tpl.content) {
      console.error('Wishlist – template introuvable: #wishlist-product-card-template');
      return null;
    }

    const node = tpl.content.firstElementChild.cloneNode(true);

    const vId = (variant.id || '').split('/').pop();
    const p = variant.product;
    const productTitle = p.title || '';
    const vTitle = variant.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : '';
    const title = `${productTitle}${vTitle}`;

    const handle = p.handle || '';
    const url = handle ? `${getLocalizedRootUrl()}products/${handle}` : '#';

    const productImages = (p.images && p.images.nodes) || [];
    const primaryImage = variant.image || productImages[0] || null;
    const secondaryImage = productImages.find(image => image && primaryImage && image.url !== primaryImage.url) || null;
    const hasHoverImage = tpl.dataset.hoverImages === 'true' && Boolean(secondaryImage);
    const variantCount = ((p.variants && p.variants.nodes) || []).length;
    const isSingleVariant = variantCount === 1;
    const isAvailable = variant.availableForSale === true;

    const amount = (variant.price && variant.price.amount)
                || (p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.amount)
                || '';
    const currency = (variant.price && variant.price.currencyCode)
                  || (p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.currencyCode)
                  || 'EUR';

    const vendor = p.vendor || '';

    // Remplissage DOM (textContent / attributs uniquement)
    const figure = node.querySelector('.product-featured-image');
    if (figure) figure.classList.toggle('thb-hover', hasHoverImage);

    const linkImg = node.querySelector('[data-field="url"]');
    if (linkImg) {
      linkImg.setAttribute('href', url);
      linkImg.setAttribute('title', productTitle);
      if (tpl.dataset.aspectRatio === 'adapt' && primaryImage?.width && primaryImage?.height) {
        linkImg.style.setProperty('--padding-bottom', `${(primaryImage.height / primaryImage.width) * 100}%`);
      }
    }

    const imgEl = node.querySelector('[data-field="image"]');
    const imageReady = setLazyImage(imgEl, primaryImage, title);
    if (!imageReady && imgEl) {
      imgEl.remove();
    }

    if (hasHoverImage && linkImg) {
      const secondaryImg = document.createElement('img');
      secondaryImg.className = 'lazyload product-secondary-image';
      if (setLazyImage(secondaryImg, secondaryImage, productTitle)) {
        linkImg.insertBefore(secondaryImg, imgEl || linkImg.firstChild);
      }
    }

    const titleEl = node.querySelector('[data-field="title"]');
    if (titleEl) {
      titleEl.textContent = title;
      titleEl.setAttribute('href', url);
    }

    const vendorEl = node.querySelector('[data-field="vendor"]');
    if (vendorEl) vendorEl.textContent = vendor;

    const priceEl = node.querySelector('[data-field="price"]');
    if (priceEl) priceEl.textContent = fmtPrice(amount, currency);

    const btn = node.querySelector('[data-field="variant-id"]');
    if (btn) {
      const productId = normalizeShopifyId(p.id);

      btn.setAttribute('data-variant-id', vId);
      if (productId) btn.setAttribute('data-product-id', productId);
      btn.setAttribute('data-product-title', productTitle);
      btn.setAttribute('data-product-handle', handle);
      btn.setAttribute('data-variant-title', variant.title || '');
      btn.setAttribute('data-wishlist-source', 'wishlist_page');
    }

    const buyAction = node.querySelector('[data-field="buy-action"]');
    if (buyAction) {
      buyAction.setAttribute('data-product-handle', handle);
      buyAction.querySelector('.product-card--add-to-cart-text').textContent = isAvailable
        ? tpl.dataset.addToCartText
        : tpl.dataset.soldOutText;

      if (isSingleVariant) {
        buyAction.classList.toggle('product-card--add-to-cart-button-simple', isAvailable);
        buyAction.setAttribute('data-product-id', vId);
        if (!isAvailable) buyAction.setAttribute('disabled', 'disabled');
      } else {
        const quickView = document.createElement('quick-view');
        quickView.className = buyAction.className;
        quickView.setAttribute('data-field', 'buy-action');
        quickView.setAttribute('data-product-handle', handle);
        quickView.setAttribute('tabindex', '-1');
        quickView.innerHTML = buyAction.innerHTML;
        quickView.querySelector('.product-card--add-to-cart-text').textContent = tpl.dataset.chooseOptionsText;
        buyAction.replaceWith(quickView);
      }
    }

    return node;
  };

  // --- Rendu de la page wishlist -----------------------------------------
  const renderWishlistPage = async () => {
  const container = document.querySelector('.wishlist-items');
  const emptyText = document.querySelector('.wishlist-empty');
  if (!container) return;

  // Reset
  container.innerHTML = '';

  // 1) Aucune ID locale → affiche l’état vide
  if (!wishlist.length) {
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  // 2) Token Storefront absent → ne bloque pas, affiche l’état vide
  if (!window.STOREFRONT_TOKEN) {
    console.warn('Wishlist: STOREFRONT_TOKEN manquant – rendu vide');
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  const variantGIDs = wishlist.map(id => `gid://shopify/ProductVariant/${id}`);

  const query = `
    query getWishlistProducts($ids: [ID!]!, $language: LanguageCode, $country: CountryCode)
    @inContext(language: $language, country: $country) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          title
          availableForSale
          image {
            url
            url20: url(transform: { maxWidth: 20 })
            url298: url(transform: { maxWidth: 298 })
            url596: url(transform: { maxWidth: 596 })
            altText
            width
            height
          }
          price { amount currencyCode }
          product {
            id
            handle
            title
            vendor
            featuredImage {
              url
              url20: url(transform: { maxWidth: 20 })
              url298: url(transform: { maxWidth: 298 })
              url596: url(transform: { maxWidth: 596 })
              altText
              width
              height
            }
            images(first: 2) {
              nodes {
                url
                url20: url(transform: { maxWidth: 20 })
                url298: url(transform: { maxWidth: 298 })
                url596: url(transform: { maxWidth: 596 })
                altText
                width
                height
              }
            }
            variants(first: 2) {
              nodes {
                id
              }
            }
          }
        }
      }
    }`;

  let nodes = [];
  try {
    const res = await fetch('/api/2025-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_TOKEN
      },
      body: JSON.stringify({
        query,
        variables: {
          ids: variantGIDs,
          language: getStorefrontLanguage(),
          country: getStorefrontCountry()
        }
      })
    });
    const result = await res.json();
    nodes = result.data?.nodes || [];
  } catch (err) {
    console.error('Erreur chargement wishlist (réseau/API):', err);
  }

  const validNodes = (nodes || []).filter(Boolean);

  // 3) Si l’API ne retourne rien → montre l’état vide AU LIEU de ne rien rendre
  if (validNodes.length === 0) {
    if (emptyText) emptyText.classList.remove('hidden', 'hidden-wishlist-text');
    updateEmptyState();
    return;
  }

  const foundIds = new Set(validNodes.map(v => String(v.id.split('/').pop())));
  validNodes.forEach(variant => {
    const card = renderWishlistCard(variant);
    if (card) container.appendChild(card);
  });

  // Purge douce
  const before = wishlist.slice();
  const next = wishlist.filter(id => foundIds.has(id));
  if (next.length !== before.length) {
    const purged = before.filter(id => !foundIds.has(id));
    console.info('Wishlist – purge confirmée par API (variants inexistants):', purged);
    wishlist = next;
    await saveWishlist();
    updateBadge();
    if (getCustomerId()) {
      syncWishlistState(wishlist).catch(err => {
        console.warn('Wishlist sync purge échouée:', err.message);
      });
    }
  }

  updateButtons();
  setTimeout(updateEmptyState, 0);
};

  // --- Init DOMReady ------------------------------------------------------
  document.addEventListener('DOMContentLoaded', async () => {
    // Nettoyage immédiat d’anciennes valeurs corrompues
    const cleaned = sanitizeIds(wishlist);
    if (!sameWishlist(cleaned, wishlist)) {
      wishlist = cleaned;
    }

    await saveWishlist();

    if (getCustomerId() && !sameWishlist(wishlist, customerWishlist)) {
      syncWishlistState(wishlist).catch(err => {
        console.warn('Wishlist sync initiale échouée:', err.message);
      });
    }

    updateButtons();
    updateBadge();

    if (document.querySelector('.wishlist-items')) {
      renderWishlistPage();
    }
  });

  // --- Listeners globaux --------------------------------------------------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.wishlist-btn');
    if (btn) toggleItem(btn.dataset.variantId, btn);
  });
})();
